import type { AriaAttributes, Ref } from 'react'
import cx from 'classnames'
import styles from './IconButton.module.css'
import { type PressEventHandler } from '@/infra/prop-types'
import { RawButton } from '@/components/Button'
import { IsSkeletonContext, useIsSkeleton } from '@/components/Skeleton'
import { match } from '@/utils/pattern-match-util'
import { isHoverSupported } from '@/utils/browser-util'
import { Icon, type IconName } from '@/components/Icon'
import { Tooltip, TooltipTrigger } from '@/components/Tooltip'
import type { Placement } from 'react-aria'

export interface IconButtonProps extends Omit<AriaAttributes, 'aria-label'> {
  /**
   * The icon to display. This should always be the first prop when using the component.
   */
  name: IconName

  /**
   * An accessible label.
   */
  label: string

  /**
   * Press handler.
   */
  onPress?: PressEventHandler

  /**
   * Ref to the underlying element.
   */
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement>

  /**
   * If specified, renders as a link to this path.
   */
  to?: string

  /**
   * If specified, renders as a link to this URL.
   */
  href?: string

  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether to render as a skeleton.
   */
  isSkeleton?: boolean

  /**
   * The variant to use.
   */
  variant?: 'default' | 'primary' | 'secondary' | 'no-hover' | 'danger'

  /**
   * When used with `to`, causes the route to be replaced rather than pushed.
   */
  replace?: boolean

  /**
   * If specified, used for receiving props based on the React Aria slot.
   */
  slot?: string

  /**
   * Whether to use the small variant.
   */
  sm?: boolean

  /**
   * Rotation angle of the icon in degrees.
   */
  rotate?: number

  /**
   * Where to place the tooltip.
   */
  tooltipPlacement?: Placement

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * A button that displays an icon. The button's size matches the icon's size.
 */
export function IconButton({
  name,
  label,
  ref,
  to,
  href,
  isSkeleton,
  isDisabled,
  replace,
  variant = 'default',
  sm,
  rotate,
  tooltipPlacement,
  testId,
  ...rest
}: IconButtonProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  isDisabled ||= isSkeleton

  const commonClassNames = cx(
    replace && styles.touchNone,
    sm && styles.sm,
    match(variant, {
      primary: styles.primary,
      'no-hover': styles.noHover,
      secondary: styles.secondary,
      danger: styles.danger,
      default: null,
    }),
  )

  const icon = (
    <Icon
      name={name}
      sm={sm}
      skeleton={isSkeleton}
      variant="inherit"
      rotate={rotate}
    />
  )

  return (
    <IsSkeletonContext value={isSkeleton}>
      <TooltipTrigger>
        <RawButton
          type="button"
          className={(p) => cx(getClassnames(p), commonClassNames)}
          ref={ref}
          to={to}
          href={href}
          replace={replace}
          isDisabled={isDisabled}
          aria-label={label}
          data-test={testId}
          {...rest}
        >
          {icon}
        </RawButton>
        <Tooltip placement={tooltipPlacement}>{label}</Tooltip>
      </TooltipTrigger>
    </IsSkeletonContext>
  )
}

function getClassnames({
  isFocusVisible,
  isHovered,
  isDisabled,
  isPressed,
}: {
  isFocusVisible: boolean
  isHovered: boolean
  isDisabled: boolean
  isPressed: boolean
}): string {
  return cx(
    styles.root,
    isFocusVisible && styles.focusVisible,
    isHovered && isHoverSupported && styles.hover,
    isPressed && styles.pressed,
    isDisabled && styles.disabled,
  )
}
